'use client';
import Link from 'next/link';
import type { ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Select } from '@/components/ui/select';
import { DrawNavigation } from './DrawNavigation';
interface DrawViewHeaderProps {
  title: string;
  backHref: string;
  currentTab: 'draws' | 'seedings';
  onTabChange: (tab: 'draws' | 'seedings') => void;
  note?: ReactNode;
}
/** One mobile control row; desktop keeps the existing title and view tabs. */
export function DrawViewHeader({
  title,
  backHref,
  currentTab,
  onTabChange,
  note,
}: DrawViewHeaderProps) {
  const t = useTranslations('Event.drawView');
  const tNav = useTranslations('Event.navigation');
  return (
    <>
      <div className="flex min-w-0 flex-wrap items-center gap-2 md:hidden">
        <Link
          href={backHref}
          aria-label={t('backToDivisions')}
          className="text-muted-foreground hover:text-primary flex size-11 shrink-0 items-center justify-center rounded-md"
        >
          <ArrowLeftIcon aria-hidden="true" className="size-4" />
        </Link>
        <h2 className="min-w-[8ch] flex-[100_1_8ch] text-base leading-snug font-semibold break-words [word-break:keep-all]">
          {title}
        </h2>
        {/* Favor title space on one line; this control moves to its own line when wrapping. */}
        <div className="min-w-0 flex-[1_1_7.5rem]">
          <Select
            variant="navigation"
            aria-label={tNav('viewLabel')}
            value={currentTab}
            onChange={(e) => onTabChange(e.target.value as 'draws' | 'seedings')}
          >
            <option value="draws">{tNav('drawView')}</option>
            <option value="seedings">{tNav('seedings')}</option>
          </Select>
        </div>
      </div>
      <div className="md:hidden">{note}</div>
      <div className="hidden min-w-0 items-center justify-between gap-4 md:flex">
        <div className="min-w-0">
          <h2 className="text-2xl font-semibold break-words lg:text-3xl">{title}</h2>
          {note}
        </div>
        <DrawNavigation currentTab={currentTab} onTabChange={onTabChange} />
      </div>
    </>
  );
}
